import { ChainData, ChainType } from "@0xsquid/squid-types";
import { StargateClient } from "@cosmjs/stargate";
import { SuiClient } from "@mysten/sui/client";
import { Connection as SolanaConnection } from "@solana/web3.js";
import { JsonRpcProvider } from "ethers";
import { getWorkingCosmosRpcUrl } from "../../services/external/rpcService";
import { StellarRpcClient } from "../../services/external/stellarRpcClient";
import { XrplRpcClient } from "../../services/external/xrplRpcClient";
import { SOLANA_RPC_URL } from "../constants";

type ChainTypeToClient = {
  [ChainType.EVM]: JsonRpcProvider;
  [ChainType.COSMOS]: StargateClient;
  [ChainType.SOLANA]: SolanaConnection;
  [ChainType.BTC]: null;
  [ChainType.SUI]: SuiClient;
  [ChainType.XRPL]: XrplRpcClient;
  [ChainType.STELLAR]: StellarRpcClient;
  [ChainType.CANTON]: null;
};

type ClientCacheKey = `${ChainType}:${string}`;

const clientCache = new Map<ClientCacheKey, ChainTypeToClient[ChainType]>();

export async function getClient<T extends ChainType>(
  chain: ChainData
): Promise<ChainTypeToClient[T]> {
  const key = `${chain.chainType}:${chain.chainId}` as const;

  if (clientCache.has(key)) {
    return clientCache.get(key) as ChainTypeToClient[T];
  }

  const client = await createClient<T>(chain);

  clientCache.set(key, client);

  return client;
}

async function createClient<T extends ChainType>(
  chain: ChainData
): Promise<ChainTypeToClient[T]> {
  switch (chain.chainType) {
    case ChainType.EVM:
      return new JsonRpcProvider(chain.rpc) as ChainTypeToClient[T];

    case ChainType.COSMOS:
      const rpcUrl = await getWorkingCosmosRpcUrl(chain);
      return (await StargateClient.connect(rpcUrl)) as ChainTypeToClient[T];

    case ChainType.SOLANA:
      return new SolanaConnection(SOLANA_RPC_URL) as ChainTypeToClient[T];

    case ChainType.BTC:
      return null as ChainTypeToClient[T];

    case ChainType.SUI:
      return new SuiClient({
        url: chain.rpc,
      }) as ChainTypeToClient[T];

    case ChainType.XRPL:
      return new XrplRpcClient(chain.rpc) as ChainTypeToClient[T];

    case ChainType.STELLAR:
      return new StellarRpcClient(chain.rpc) as ChainTypeToClient[T];

    case ChainType.CANTON:
      return null as ChainTypeToClient[T];
  }
}
